import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { OnlineClass } from '../types/api';
import { onlineClassService, OnlineClassServiceError } from '../services/onlineClassService';

interface OnlineClassContextType {
    onlineClasses: OnlineClass[];
    featuredClasses: OnlineClass[];
    upcomingClasses: OnlineClass[];
    loading: boolean;
    error: string | null;
    refreshOnlineClasses: () => Promise<void>;
}

const OnlineClassContext = createContext<OnlineClassContextType | undefined>(undefined);

export function OnlineClassProvider({ children }: { children: ReactNode }) {
    const [onlineClasses, setOnlineClasses] = useState<OnlineClass[]>([]);
    const [featuredClasses, setFeaturedClasses] = useState<OnlineClass[]>([]);
    const [upcomingClasses, setUpcomingClasses] = useState<OnlineClass[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refreshOnlineClasses = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('OnlineClassContext: Fetching online classes');
            
            // Fetch all classes
            const classes = await onlineClassService.getOnlineClasses();
            console.log('OnlineClassContext: Received online classes:', classes);
            setOnlineClasses(classes);
            
            // Get featured classes
            const featured = classes.filter(cls => cls.isFeatured);
            console.log('OnlineClassContext: Featured classes:', featured);
            setFeaturedClasses(featured);
            
            // Get upcoming classes - only show featured classes
            const now = new Date();
            const upcoming = classes
                .filter(cls => cls.startDate > now && cls.isFeatured)
                .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
            console.log('OnlineClassContext: Upcoming classes:', upcoming);
            setUpcomingClasses(upcoming);
            
        } catch (error: any) {
            console.error('OnlineClassContext: Error fetching online classes:', error);
            setError(error instanceof OnlineClassServiceError ? error.message : 'Failed to load online classes');
        } finally {
            setLoading(false);
        }
    };



    // Load data on mount
    useEffect(() => {
        refreshOnlineClasses();
    }, []);

    return (
        <OnlineClassContext.Provider value={{ 
            onlineClasses, 
            featuredClasses,
            upcomingClasses,
            loading, 
            error, 
            refreshOnlineClasses,

        }}>
            {children}
        </OnlineClassContext.Provider>
    );
}

export function useOnlineClasses() {
    const context = useContext(OnlineClassContext);
    if (context === undefined) {
        throw new Error('useOnlineClasses must be used within an OnlineClassProvider');
    }
    return context;
} 